import { Component, ElementRef, Input, OnChanges, OnDestroy, QueryList, SimpleChanges, ViewChild, ViewChildren } from '@angular/core';
import { NgbDate, NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { isEqual, zip } from 'lodash';
import { Chart, ChartData, ChartOptions, ScriptableContext } from 'chart.js';
import { enGB, fi } from 'date-fns/locale';
import { BehaviorSubject, combineLatest, concat, from, merge, Observable, of, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, shareReplay, switchMap, take, takeUntil } from 'rxjs/operators';
import { MapService } from '~app/shared/map/map.service';
import { TranslateService } from '@ngx-translate/core';
import { ChartJsComponent } from '~app/components/chart-js/chart-js.component';
import { format } from 'date-fns';
import { environment } from '~environments/environment';
import { DefaultMap } from '~app/shared/utils';
import { InfluxMetadataService, MetadataType } from '../influx-metadata/influx-metadata.service';
import { InfluxMetadataMisonetService } from '../influx-metadata/influx-metadata-misonet.service';
import { InfluxMetadataV2lService } from '../influx-metadata/influx-metadata-v2l.service';
import __ from 'lodash';
import { _ } from '~app/shared/utils';


type Data = ChartData<'line', number[], string>;
type RawData = [string, string|number|boolean, string?];

interface FilterSubjects {
  value: BehaviorSubject<string>;
  click: Subject<string>;
  focus: Subject<string>;
}

// the new Clipboard API isn't included in TypeScript yet, but PARTS of it are available on Chrome
// based on https://developer.mozilla.org/en-US/docs/Web/API/ClipboardItem
// only `Blob` works in Chrome for now
declare class ClipboardItem {
  constructor(ClipboardItemData: { [mimeType: string]: Blob });
}
// based on https://developer.mozilla.org/en-US/docs/Web/API/Clipboard
interface ChromeClipboard extends Clipboard {
  write(data: ClipboardItem[]): Promise<void>;
}

//// "hand-picked" colors
const darkColors = [
  'rgb(54, 162, 235)', //// blue
  'rgb(255, 99, 132)', //// red
  'rgb(218, 112, 214)',//// purple
  'rgb(255, 205, 86)', //// yellow
  'rgb(0, 153, 0)',    //// green
  'rgb(255, 159, 64)', //// orange
  'rgb(201, 203, 207)' //// grey
];
const lightColors = [
  'rgb(220, 240, 255)', //// light-blue
  'rgb(255, 230, 230)', //// light-red
  'rgb(250, 230, 250)', //// light-purple
  'rgb(255, 229, 170)', //// light-yellow
  'rgb(136, 249, 136)', //// light-green
  'rgb(255, 213, 169)', //// light-orange
  'rgb(233, 234, 236)'  //// light-grey
];
enum colors {
  LIGHT,
  DARK
};
const datasetColor = (type:colors, index:number): string => {
  switch (type) {
    case colors.DARK:
      return darkColors[index] ? darkColors[index]: darkColors[0];
    case colors.LIGHT:
      return lightColors[index] ? lightColors[index] : lightColors[0];
    default:
      return darkColors[0];
  }
}

@Component({
  selector: 'qm-influx-graph',
  templateUrl: './influx-graph.component.html',
  styleUrls: ['./influx-graph.component.scss']
})
export class InfluxGraphComponent implements OnDestroy, OnChanges {
  @Input() /** InfluxDB Cloud bucket name */
  public bucket: string;
  @Input() /** InfluxDB `_measurement` */
  public measurement: string;
  @Input() /** Names of InfluxDB tags/fields to provide filter dropdowns for, in order of specificity. */
  public filterFields: string[];
  @Input() /** Label fields for filter fields. */
  public filterLabelFields?: { [key: string]: string };
  @Input() /** Default values for filters. Also used when resetting filter to the right of a changed filter. */
  public filterDefaults?: { [key: string]: string };
  @Input() /** Field containing window function name. If not specified, 'mean' is used. */
  public windowField?: string;
  @Input()
  public metadataType: MetadataType;
  @Input() /** Default field from where to search for, varies between datasources */
  public unitFieldName?: string;

  private readonly metadataServices: { [key in MetadataType]: InfluxMetadataService };
  private get metadataService(): InfluxMetadataService {
    return this.metadataServices[this.metadataType];
  }

  @ViewChildren('filterInputs') /** References to the filter <input>s, to allow focusing them programmatically */
  private readonly filterInputs: QueryList<ElementRef<HTMLInputElement>>;

  @ViewChild(ChartJsComponent)
  private readonly chart: ChartJsComponent<'line'>;

  @ViewChild('clipboardPopover')
  private readonly clipboardPopover?: NgbPopover;
  public readonly clipboardSupported: boolean = navigator.clipboard !== undefined;

  private readonly ngUnsubscribe = new Subject<void>();

  /** Emits when the set of available fields has changed */
  private readonly fieldsChanged$ = new Subject<void>();

  /** Map of field name (one of `filterFields`) to an object containing observables related to that field */
  public readonly filter$s = new Map<string, FilterSubjects>();

  /** Data to be rendered into the graph */
  public readonly data$ = new BehaviorSubject<Data | null>(null);
  public readonly hasData$ = this.data$.pipe(map(data => data?.datasets?.length > 0), takeUntil(this.ngUnsubscribe));
  /** Chart.js options */
  public readonly chartOptions$ = new BehaviorSubject<ChartOptions<'line'>>({});

  /** Date range chosen by the user */
  public readonly dateRangeSubject = new Subject<[NgbDate, NgbDate]>();
  public readonly dateRange$: Observable<{ _start: string, _stop: string }> = this.dateRangeSubject.pipe(
    map(([start, stop]) => ({
      _start: start && new Date(start.year, start.month - 1, start.day).toISOString?.(),
      _stop: stop && new Date(stop.year, stop.month - 1, stop.day + 1).toISOString?.(),
    })),
    // one of the dates was missing/invalid (could happen when the user is in the process of selecting a new range)
    filter(({ _start, _stop }) => _start !== undefined && _stop !== undefined),
    takeUntil(this.ngUnsubscribe),
    shareReplay(1),
  );

  // cache long-lived observables for autocomplete values to reduce backend requests and improve UI responsiveness
  // the map is manually cleared whenever the set of available fields has changed
  private possibleFieldValues$s = new DefaultMap<string, Observable<string[]>>(fieldName => {
    const precedingFieldNames = this.filterFields.slice(0, this.filterFields.indexOf(fieldName));
    return combineLatest([this.filterValues$(precedingFieldNames), this.dateRange$]).pipe(
      switchMap(([filters,daterange]) => {
        // only query server if all preceding filters are filled in
        if (!Array.from(filters.values()).every(x => x)) {
          return of([] as [string, string][]);
        }

        const labelField = this.filterLabelFields?.[fieldName];
        if (labelField) {
          return from(this.metadataService.getDistinctValuesWithLabel(
            fieldName,
            labelField,
            Object.fromEntries(filters)
          )).pipe(
            map(values => Array.from(values.entries()).map(([key, value]) => [key.toString(), value.toString()] as const))
          );
        }

        return from(this.metadataService.getDistinctValues(
          fieldName,
          Object.fromEntries(filters),
          daterange
        )).pipe(
          // make non-labeled fields have the same format as labeled ones
          map(values => Array.from(values).map(value => [value.toString(), value.toString()] as const))
        );
      }),
      map(options => options
        .map(([key, value]) => key === value ? key : `${key} > ${value}`)
        // InfluxDB sorts alphabetically, but we want trailing numbers to be sorted naturally
        .sort((a, b) => a.toString().localeCompare(b.toString(), this.translateService.currentLang, { numeric: true }))
      ),
      takeUntil(this.fieldsChanged$),
      takeUntil(this.ngUnsubscribe),
      shareReplay(1),
    );
  });

  public disabledFor = (fieldName: string): Observable<boolean> => {
    const previousFieldName = this.filterFields[this.filterFields.indexOf(fieldName) - 1];
    if (!previousFieldName) {
      return of(false);
    }
    return this.filter$s.get(previousFieldName).value.pipe(
      map(value => !value),
    );
  }

  public searchFor = (fieldName: string) => {
    return (text$: Observable<string>): Observable<string[]> => {
      return combineLatest([
        merge(
          text$.pipe(debounceTime(100), distinctUntilChanged()),
          this.filter$s.get(fieldName).click,
          this.filter$s.get(fieldName).focus,
        ),
        this.possibleFieldValues$s.get(fieldName),
      ]).pipe(
        map(([text, options]) => {
          if (text) {
            text = text.toLowerCase();
            options = options.filter(option => option.toLowerCase().includes(text));
          }

          const MAX_RESULTS = 10_000; // must be even

          // return early to avoid having to think about corner cases for short lists
          if (options.length <= MAX_RESULTS) {
            // always returning a new list seems to be required to get proper updates
            return options.slice();
          }

          const currentIndex = options.indexOf(this.filter$s.get(fieldName).value.getValue());
          if (currentIndex === -1) {
            return options.slice(0, MAX_RESULTS);
          }

          // The rest is for slicing the list such that there are always MAX_RESULTS items,
          // even when the selected item is near the end of the list.

          const startIndex = currentIndex - MAX_RESULTS / 2; // inclusive
          const endIndex = currentIndex + MAX_RESULTS / 2; // exclusive

          // near the start of the list
          if (startIndex < 0) {
            return options.slice(0, MAX_RESULTS);
          }

          // near the end of the list
          if (endIndex > options.length) {
            return options.slice(-MAX_RESULTS);
          }

          return options.slice(startIndex, endIndex);
        }),
        takeUntil(this.ngUnsubscribe),
      );
    };
  }

  constructor(
    metadataMisonetService: InfluxMetadataMisonetService,
    metadataV2lService: InfluxMetadataV2lService,
    private readonly mapService: MapService,
    readonly translateService: TranslateService,
  ) {
    this.metadataServices = {
      misonet: metadataMisonetService,
      v2l: metadataV2lService
    };

    const filters$ = combineLatest([
      this.dateRange$,
      this.fieldsChanged$,
    ]).pipe(
      // get latest filter values
      switchMap(([range]) => from(this.filterValues$(Array.from(this.filter$s.keys()))).pipe(
        map(filters => [filters, range] as const),
      )),
    );

    // data pipeline ->
    const datas$ = filters$.pipe(
      switchMap(([filters, range]) => {
        // if not all filters are filled in, show nothing
        if (Array.from(filters.values()).includes('')) {
          console.log('empty filters values included, NOP');
          return of<null>(null);
        }

        const rangeDays = (new Date(range._stop).getTime() - new Date(range._start).getTime()) / (1000 * 60 * 60 * 24);
        const filtersObject = Object.fromEntries(filters);
        return concat(
          of<null>(null), // start with null to hide out-of-date data during loading
          combineLatest([
            this.metadataService.resolveTimelineFilters(filtersObject).then(resolvedFilters => this.metadataService.getTimeline(
              this.bucket,
              this.measurement,
              {
                ...resolvedFilters,
                ...range,
                _window:
                  rangeDays <= 31 ? '1h' :
                  rangeDays <= 365 ? '1d' :
                  `${Math.floor(rangeDays / 365)}d`,
                _windowFunction: this.windowField && this.metadataService.resolveWindowFunction(filtersObject[this.windowField]) || 'mean',
                _windowOffset: `${Math.floor(new Date(range._start).getTimezoneOffset()).toString()}m`,
              },
            ).catch(err => {
              console.error('InfluxGraphComponent getTimeline failed', err);
              return null;
            })),
            //// filters selection values needed for labeling
            of(filtersObject)
          ]
          ),
        );
      }),
      takeUntil(this.ngUnsubscribe),
    );
    datas$.pipe(
      filter(res => (res !== null && res[0] !== null)),
      map((results: [RawData][]): Data => (
        {
        // line datapoints
        //// https://www.chartjs.org/docs/latest/general/data-structures.html#object
        datasets: results[0]
          ?
            __(results[0])
            .groupBy((x: RawData) => x[2]??`${results[1]['tunnus']} ${results[1]['symboli']}`)
            .map((value: any, key: string) => ({
              label: key,
              data: value.map((ee:RawData) => ({x: ee[0], y:ee[1] as number})),
              backgroundColor: (context: ScriptableContext<'line'>) => {
                return datasetColor(colors.LIGHT, context.datasetIndex);
              },
              borderColor:  (context: ScriptableContext<'line'>) => {
                return datasetColor(colors.DARK, context.datasetIndex);
              },
              pointBackgroundColor: (context: ScriptableContext<'line'>) => {
                return datasetColor(colors.LIGHT, context.datasetIndex);
              },
              pointBorderColor: (context: ScriptableContext<'line'>) => {
                return datasetColor(colors.DARK, context.datasetIndex);
              }
            }))
            .value()
          : [],
      })),
      takeUntil(this.ngUnsubscribe),
    ).subscribe(this.data$);
    //// <- data pipeline

    // chart options pipeline
    //// data already is included in V2L
    filters$.pipe(
      switchMap(([filters, range]) => {
        // if not all filters are filled in, don't try to load units
        if (Array.from(filters.values()).includes('')) {
          return of([[] as string[], range] as const);
        }

        return concat(
          of([[] as string[], range] as const), // start with empty units to hide out-of-date data during loading
          from(this.metadataService.getDistinctUnits(this.unitFieldName, Object.fromEntries(filters))).pipe(
            map(units => [Array.from(units), range] as const),
          ),
        );

      }),
      map(([units, range]): ChartOptions<'line'> => ({
        locale: translateService.currentLang || 'en-GB',
        datasets: {
          line: {
            pointRadius: 2,
            borderWidth: 1,
            pointBorderWidth: 1,
          },
        },
        scales: {
          xAxis: {
            type: 'time',
            min: Date.parse(range._start),
            max: Date.parse(range._stop),
            adapters: {
              date: {
                locale: translateService.currentLang === 'fi' ? fi : enGB,
              }
            },
            time: {
              displayFormats: {
                hour: 'cccccc d.M. H:mm',
                minute: 'cccccc d.M. H:mm',
                day: 'cccccc d.M.',
              },
              tooltipFormat: 'cccc d.M.yyyy H:mm',
            }
          },
          yAxis: {
            ticks: {
              callback: value => [
                // include at most 2 decimal digits
                typeof value === 'number' ? value.toFixed(2).replace(/\.?0+$/, '') : value,
                // add unit suffix, format m³ nicely, drop empty unit marker
                units.filter(val => val !== '-').join(', ').replace(/\bm3\b/g, 'm\u00B3'),
              ].join(' ')
            },
          },
        },
        plugins: {
          legend: {
            display: true
          },
          //// show tooltip all the time, with all the decimals included
          tooltip: {
            intersect: false,
            callbacks: {
              label: context => `${context.dataset.label} : ${context.parsed.y.toString()}`,
            }
          }
        },
      })),
      takeUntil(this.ngUnsubscribe),
    ).subscribe(this.chartOptions$);
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    // this stuff has to be synchronously handled due to template rendering, so it's not done through the Observable
    if (changes.filterFields || changes.filterDefaults) {
      this.filter$s.clear();
      let previousFieldName = null;
      for (const fieldName of this.filterFields) {
        this.filter$s.set(fieldName, {
          value: new BehaviorSubject(this.filterDefaults?.[fieldName] ?? ''),
          click: new Subject(),
          focus: new Subject(),
        });
        // reset fields to the right when a value is changed, unless the old value is still possible
        if (previousFieldName) {
          this.filter$s.get(previousFieldName).value.pipe(
            switchMap(() => this.possibleFieldValues$s.get(fieldName)),
            takeUntil(this.ngUnsubscribe),
          ).subscribe(options => {
            const value$ = this.filter$s.get(fieldName).value;
            // call .next() even if the value hasn't changed, to cascade the check to the right
            value$.next(
              options.includes(value$.getValue())
              ? value$.getValue()
              : (this.filterDefaults?.[fieldName] ?? '')
            );
          });
        }
        previousFieldName = fieldName;
      }

      this.possibleFieldValues$s.clear();
      this.fieldsChanged$.next();
    }
  }

  /** @returns An Observable containing the latest (possibly empty) values for the given filter fields */
  private filterValues$(names: string[], withLabel: boolean = false): Observable<Map<string, string>> {
    if (!names || names.length === 0) {
      return of(new Map());
    }
    return combineLatest(
      // extract the unlabeled value of each option
      // yes it's ugly, but dealing with a [string, string][] got out of hand due to the bi-directional data flow
      names.map(name => {
        const value$ = this.filter$s.get(name).value;
        return withLabel ? value$ : value$.pipe(map(value => value.match(/^(.+) > /)?.[1] ?? value));
      }),
    ).pipe(
      map(values => new Map(zip(names, values))),
      distinctUntilChanged(isEqual),
      takeUntil(this.ngUnsubscribe),
    );
  }

  /** Prefill filters based on current map selection. */
  public loadCurrentFeature() {
    this.mapService.currentFeature$.pipe(
      take(1),
      takeUntil(this.ngUnsubscribe),
    ).subscribe(feature => {
      if (!feature) {
        // nothing is selected
        return;
      }

      // fill in order so that loading logic works normally
      for (const fieldName of this.filterFields) {
        const propertyName = feature.layer?.metadata?.measurementsFilters?.[fieldName];
        if (propertyName) {
          //// MISONET data with layer metadata propertyName
          this.filter$s.get(fieldName).value.next(feature.properties[propertyName]);
        } else if (feature.properties[fieldName]) {
          //// V2L data directly with fieldName
          this.filter$s.get(fieldName).value.next(feature.properties[fieldName]);
        }
      }

      // after Observables have settled, focus first non-filled field to load dropdown
      const firstEmptyName = this.filterFields.find(fieldName => !this.filter$s.get(fieldName).value.getValue());
      if (firstEmptyName) {
        setTimeout(() => {
          this.filterInputs
            .map(ref => ref.nativeElement)
            .find(el => el.dataset.fieldName === firstEmptyName)
            ?.focus?.();
        }, 0);
      }
    });
  }

  /** @returns A PNG image showing the values of the selected filters, and the content of the currently rendered graph. */
  private async captureImage(): Promise<Blob> {
    const LABEL_SIZE = 14;
    const LABEL_MARGIN = 5;

    // toPromise returns the last value, so use take(1) to force Observable completion immediately
    const filterValues = await this.filterValues$(Array.from(this.filter$s.keys()), true).pipe(take(1)).toPromise();
    const range = await this.dateRange$.pipe(take(1)).toPromise();

    const chartCanvas = this.chart.canvas.nativeElement;
    const captureCanvas = new OffscreenCanvas(
      chartCanvas.width,
      // chart height + top margin + text height and margin for each filter and the range
      chartCanvas.height + LABEL_MARGIN + (LABEL_SIZE + LABEL_MARGIN) * (filterValues.size + 1),
    );
    const context = captureCanvas.getContext('2d');

    // add background color (default is transparency)
    context.fillStyle = 'white';
    context.fillRect(0, 0, captureCanvas.width, captureCanvas.height);

    // add REGO logo
    const logo = new Image();
    const logoDrawn = new Promise<void>((resolve, reject) => {
      logo.addEventListener('load', () => {
        try {
          context.drawImage(logo, chartCanvas.width - logo.width, 0);
          resolve();
        } catch (err) {
          reject(err);
        }
      }, false);
      logo.addEventListener('error', err => {
        reject(err);
      }, false);
    });
    logo.src = `${environment.assetRoot}/img/rego.png`;

    try {
      await logoDrawn;
    } catch (err) {
      // failing to add the logo shouldn't prevent creation of the rest of the image
      console.error('Error adding REGO logo to image', err);
    }

    // copy chart image to the bottom of the target image
    context.drawImage(chartCanvas, 0, captureCanvas.height - chartCanvas.height);

    // add filter labels in order
    context.fillStyle = 'black';
    context.textBaseline = 'top';
    context.font = `${LABEL_SIZE}px sans-serif`;
    let y = LABEL_MARGIN;
    for (const fieldName of this.filterFields) {
      context.fillText(filterValues.get(fieldName).toString(), LABEL_MARGIN, y);
      y += LABEL_SIZE + LABEL_MARGIN;
    }

    const formatDate = (date: string): string => {
      return format(new Date(date), 'cccccc d.M.yyyy', {
        locale: this.translateService.currentLang === 'fi' ? fi : enGB,
      });
    };
    // \u2013 = en dash
    context.fillText(`${formatDate(range._start)} \u2013 ${formatDate(range._stop)}`, LABEL_MARGIN, y);

    return captureCanvas.convertToBlob({ type: 'image/png', quality: 1.0 });
  }

  /** Saves the current graph onto the clipboard as a PNG image. */
  public async saveToClipboard(): Promise<void> {
    try {
      // clipboard-write SHOULD be available whenever the tab has focus, but check just in case
      const perm = await navigator.permissions.query({ name: 'clipboard-write' as PermissionName });
      if (perm.state === 'denied') {
        throw new Error('Access to clipboard denied.');
      }

      (navigator.clipboard as ChromeClipboard).write([
        new ClipboardItem({
          'image/png': await this.captureImage(),
        }),
      ]);

      this.clipboardPopover.open();
      setTimeout(() => this.clipboardPopover.close(), 1000 * 2);
    } catch (error) {
      console.error(error);
      alert('Error copying image to clipboard! Try downloading the image instead.');
    }
  }

  /** Triggers a download of the current graph as a PNG image. */
  public async saveToFile(): Promise<void> {
    const filterValues = await this.filterValues$(Array.from(this.filter$s.keys())).pipe(take(1)).toPromise();
    const range = await this.dateRange$.pipe(take(1)).toPromise();
    const filename: string[] = this.filterFields.map(fieldName => filterValues.get(fieldName))
      .concat([range._start, range._stop].map(date => date.replace(/T.+$/, '')));

    let url: string;
    try {
      url = URL.createObjectURL(await this.captureImage());

      const a = document.createElement('a');
      a.href = url;
      a.download = `${filename.join(' ')}.png`;

      await new Promise<void>((resolve, reject) => {
        try {
          a.addEventListener('click', () => {
            setTimeout(() => resolve(), 0);
          });

          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        } catch (error) {
          reject(error);
        }
      });
    } catch (error) {
      console.error(error);
      alert('Unable to download image!');
    } finally {
      if (url) {
        URL.revokeObjectURL(url);
      }
    }
  }

  /** Change the value of a filter by the given amount in the given direction. */
  public async nudgeFilter(fieldName: string, amount: number): Promise<void> {
    const options = await this.possibleFieldValues$s.get(fieldName).pipe(take(1)).toPromise();
    if (options.length === 0) {
      return;
    }

    const currentValue = this.filter$s.get(fieldName).value.getValue();
    let currentIndex = options.indexOf(currentValue);
    if (currentIndex === -1) {
      // treat invalid (or empty) selection as an option just beyond the actual scope
      currentIndex = amount < 0 ? options.length : -1;
    }

    // clamp newIndex in the available range
    let newIndex = (currentIndex + amount) % options.length;
    while (newIndex < 0) {
      newIndex += options.length;
    }

    this.filter$s.get(fieldName).value.next(options[newIndex]);
  }

  /**
   * data (fieldname) needs to be translated
   * @param text
   * @returns
   */
  public translate(text: string) {
    //// field 1
    if (text==='tunnus' || text==='NaytteenottoMatka.Naytteenotto.NaytteenottoPaikka.properties.nimi' || text === 'NaytteenottoMatka.Naytteenotto.Nayte.NayteNimi') {
      return this.translateService.instant(_('Location'));
    }
    //// field 2
    else if (text==='symboli' || text==='NaytteenottoMatka.Naytteenotto.Nayte.Analyysitulos.MaaritysmenetelmaNimi') {
      return this.translateService.instant(_('Measurement'));
    }
    //// field 3
    else if (text==='_field') {
      return this.translateService.instant(_('Aggregation'));
    }
    //// not a known field
    else {
      return this.translateService.instant(_('Unknown'));
    }
  }
}

